import graphqlClient from "@/GQLClient";
import {
  type CategoryInput,
  SaveCategoryDocument,
  Category_Type,
  GetActivitiesCategoriesDocument,
  DeleteCategoryDocument,
  GetActivitiesCountOnRelatedCategoryDocument,
} from "@/graphql/generated-types";
import { useAuthStore } from "@/stores/auth.module";
import SentryGraphQLError from "@/helpers/exceptions/SentryGraphQLError";
import type { CategoryFragment } from "@/graphql";
import type { ICategoryAPI } from "@/interfaces";

const getCategories = async () => {
  const authStore = useAuthStore();
  const institutionId = authStore.institutionId;

  const response = await graphqlClient.query({
    query: GetActivitiesCategoriesDocument,
    variables: {
      institutionId,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.activity?.getCategories) {
    return response.data.activity.getCategories as CategoryFragment[];
  }

  throw new SentryGraphQLError(
    `api.activitiyCategories.getCategories`,
    response,
  );
};

const save = async (category: CategoryInput) => {
  const response = await graphqlClient.query({
    query: SaveCategoryDocument,
    variables: {
      category,
      type: Category_Type.Activity,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.category?.save?.success) {
    return response.data.category.save;
  }

  throw new SentryGraphQLError(`api.activitiyCategories.save`, response);
};

const deleteItem = async (id: string) => {
  const response = await graphqlClient.query({
    query: DeleteCategoryDocument,
    variables: {
      id,
      type: Category_Type.Activity,
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (response.data.category?.delete?.success) {
    return response.data.category.delete;
  }

  throw new SentryGraphQLError(`api.activitiyCategories.deleteItem`, response);
};

const getEntityCountOnRelatedCategory = async (
  categoryId: number,
): Promise<number> => {
  const authStore = useAuthStore();
  const institutionId = authStore.institutionId;

  const response = await graphqlClient.query({
    query: GetActivitiesCountOnRelatedCategoryDocument,
    variables: {
      institutionId,
      ids: [categoryId.toString()],
    },
    fetchPolicy: "no-cache",
    errorPolicy: "all",
  });

  if (
    response.data.activity?.getCategories &&
    response.data.activity.getCategories.length
  ) {
    const [category] = response.data.activity.getCategories;
    return category?.entities?.totalCount || 0;
  }

  throw new SentryGraphQLError(
    `api.activitiyCategories.getEntityCountOnRelatedCategory`,
    response,
  );
};

export default {
  getCategories,
  save,
  deleteItem,
  getEntityCountOnRelatedCategory,
} as ICategoryAPI;
